import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import StorageBox from "Store/StorageBox.js";
import StorageKeys from "Utility/StorageKey.js";
import AxiosWidgetChurch from "./Axios";
import UserBaseUtils from "Utility/UserBase.js";

const ChurchMembers = () => {
  const [userJoin, setUserJoin] = useState<string[]>([]);
  const [userPending, setUserPending] = useState<string[]>([]);
  const [churchName, setChurchName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const loadDataFromStorage = () => {
    // Get data from StorageBox
    const join = StorageBox.getChurchUserJoin();
    const pending = StorageBox.getChurchUserPending();
    const name = StorageBox.getChurchName();

    console.log("ChurchMembers - Loading from StorageBox:", {
      join,
      pending,
      name,
    });

    setUserJoin(Array.isArray(join) ? join : []);
    setUserPending(Array.isArray(pending) ? pending : []);
    setChurchName(name || "");
  };

  useEffect(() => {
    // Load data on mount
    loadDataFromStorage();

    // Listen for church data updates from CreateChurchTable
    const handleChurchDataUpdate = () => {
      console.log(
        "ChurchMembers - Received churchDataUpdated event, refreshing..."
      );
      loadDataFromStorage();
    };

    window.addEventListener("churchDataUpdated", handleChurchDataUpdate);
    return () => {
      window.removeEventListener("churchDataUpdated", handleChurchDataUpdate);
    };
  }, []);

  const getProfileImage = (username: string): string => {
    return `${StorageKeys.SupabaseUrlProfile}${username}`;
  };

  const handleAcceptUser = async (username: string) => {
    if (!churchName) {
      alert("Church name not found");
      return;
    }

    setLoading(true);
    try {
      const result = await AxiosWidgetChurch.acceptUser(churchName, username);

      if (result.success) {
        // Update local state
        setUserPending((prev) => prev.filter((user) => user !== username));
        setUserJoin((prev) => {
          if (!prev.includes(username)) {
            return [...prev, username];
          }
          return prev;
        });

        // Refresh church data from database
        const user = UserBaseUtils.getUserName();
        const updatedChurchData = await AxiosWidgetChurch.getChurch(user);

        if (updatedChurchData.data && updatedChurchData.data.length > 0) {
          const updatedChurch = updatedChurchData.data.find(
            (church: any) => church.name === churchName
          );

          if (updatedChurch) {
            // Update StorageBox with latest data
            StorageBox.inputChurchDetails({
              name: updatedChurch.name,
              introduction: updatedChurch.introduction,
              imagesName: updatedChurch.imagesName,
              id: updatedChurch.id,
              personCreate: updatedChurch.personCreate,
              userJoin: updatedChurch.userJoin || [],
              userPending: updatedChurch.userPending || [],
            });

            // Update component state
            const join = Array.isArray(updatedChurch.userJoin)
              ? updatedChurch.userJoin
              : typeof updatedChurch.userJoin === "string"
              ? JSON.parse(updatedChurch.userJoin)
              : [];
            const pending = Array.isArray(updatedChurch.userPending)
              ? updatedChurch.userPending
              : typeof updatedChurch.userPending === "string"
              ? JSON.parse(updatedChurch.userPending)
              : [];

            setUserJoin(join);
            setUserPending(pending);

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent("churchDataUpdated"));
          }
        }
      } else {
        alert(`Error accepting user: ${result.error}`);
      }
    } catch (error) {
      console.error("Error accepting user:", error);
      alert("An error occurred while accepting the user");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (username: string) => {
    if (!churchName) {
      alert("Church name not found");
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to remove ${username} from this church?`
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const result = await AxiosWidgetChurch.removeMember(churchName, username);

      if (result.success) {
        // Update local state
        setUserJoin((prev) => prev.filter((user) => user !== username));

        // Refresh church data from database
        const user = UserBaseUtils.getUserName();
        const updatedChurchData = await AxiosWidgetChurch.getChurch(user);

        if (updatedChurchData.data && updatedChurchData.data.length > 0) {
          const updatedChurch = updatedChurchData.data.find(
            (church: any) => church.name === churchName
          );

          if (updatedChurch) {
            // Handle both userJoin and UserJoin (case sensitivity)
            const userJoinData =
              updatedChurch.userJoin || updatedChurch.UserJoin || [];

            // Update StorageBox with latest data
            StorageBox.inputChurchDetails({
              name: updatedChurch.name,
              introduction: updatedChurch.introduction,
              imagesName: updatedChurch.imagesName,
              id: updatedChurch.id,
              personCreate: updatedChurch.personCreate,
              userJoin: userJoinData,
              userPending:
                updatedChurch.userPending || updatedChurch.UserPending || [],
            });

            // Update component state
            const join = Array.isArray(userJoinData)
              ? userJoinData
              : typeof userJoinData === "string"
              ? JSON.parse(userJoinData)
              : [];

            setUserJoin(join);

            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent("churchDataUpdated"));
          }
        }
      } else {
        alert(`Error removing member: ${result.error}`);
      }
    } catch (error) {
      console.error("Error removing member:", error);
      alert("An error occurred while removing the member");
    } finally {
      setLoading(false);
    }
  };

  // Combine all users (pending and joined) for display
  const allUsers = [
    ...userPending.map((user) => ({ username: user, status: "pending" })),
    ...userJoin.map((user) => ({ username: user, status: "member" })),
  ];

  return (
    <div className="m-5 rounded-lg p-4 shadow-md">
      <h2 className="mb-6 text-2xl font-bold text-gray-200">
        Church Members - {churchName || "no church selected"}
      </h2>

      {allUsers.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500">No members or pending requests</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {allUsers.map((user) => {
            const isPending = user.status === "pending";
            const isMember = user.status === "member";

            return (
              <div
                key={user.username}
                className="flex flex-col items-center rounded-lg border border-gray-700 bg-gray-700 p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                {/* Profile Picture */}
                <div className="relative mb-3">
                  <img
                    src={getProfileImage(user.username)}
                    alt={user.username}
                    className="h-20 w-20 rounded-full object-cover ring-2 ring-gray-200"
                    onError={(e) => {
                      // Fallback to default avatar if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Ccircle cx='40' cy='40' r='40' fill='%23E5E7EB'/%3E%3Cpath d='M40 20c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10zm0 25c-7.7 0-23 3.9-23 11.7V60h46v-3.3c0-7.8-15.3-11.7-23-11.7z' fill='%239CA3AF'/%3E%3C/svg%3E`;
                    }}
                  />
                  {isPending && (
                    <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-yellow-400 text-xs font-bold text-white">
                      !
                    </div>
                  )}
                </div>

                {/* Username */}
                <Link
                  to={`/admin/profile?user=${encodeURIComponent(
                    user.username
                  )}`}
                  className="mb-2 text-center text-sm font-medium text-gray-200 hover:text-gray-100 hover:underline"
                >
                  {user.username}
                </Link>

                {/* Pending Badge */}
                {isPending && (
                  <div className="mb-2 rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                    Pending
                  </div>
                )}

                {/* Button */}
                {isPending ? (
                  <button
                    onClick={() => handleAcceptUser(user.username)}
                    disabled={loading}
                    className="w-full rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? "Processing..." : "Accept"}
                  </button>
                ) : isMember ? (
                  <>
                    <button
                      disabled
                      className="w-full cursor-not-allowed rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-gray-600"
                    >
                      <span className="text-gray-200">Is a member</span>
                    </button>
                    <button
                      onClick={() => handleRemoveMember(user.username)}
                      disabled={loading}
                      className="mt-2 w-full whitespace-nowrap rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? "Processing..." : "Remove Member"}
                    </button>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChurchMembers;
